import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { TeamEntity } from './team.entity';

/**
 * Why a client is on a team. `manual` WINS over `rule`: a rule re-run must
 * never destroy a deliberate exception, and without recording which is which
 * there is no way to tell them apart afterwards.
 */
export const TEAM_CLIENT_CAUSES = ['rule', 'manual'] as const;

export type TeamClientCauseModel = (typeof TEAM_CLIENT_CAUSES)[number];

/**
 * A client covered by a team (PD-16310).
 *
 * This table is MATERIALIZED — the rule is evaluated when it changes or when
 * the client changes, not on every access check. Three reasons: access
 * resolution stays a plain join rather than a rule evaluation; "why can this
 * person see this account" is answerable with a SELECT; and the rule preview
 * is the same code path as the real run rather than a separate estimate.
 */
@Index('team_client__fk_team__id', ['fkTeamId'], {})
@Index('team_client__fk_client__id', ['fkClientId'], {})
@Entity('team_client')
export class TeamClientEntity extends BaseEntity {
	@Column('int', { name: 'fk_team__id', unsigned: true })
	public fkTeamId!: number;

	@ManyToOne(() => TeamEntity, (team) => team.teamClients, {
		onDelete: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_team__id', referencedColumnName: 'id' }])
	public fkTeam!: TeamEntity;

	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@ManyToOne(() => ClientEntity, { onDelete: 'CASCADE' })
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@Column({
		name: 'assignmentCause',
		type: 'enum',
		enum: TEAM_CLIENT_CAUSES,
		default: "'rule'",
	})
	public assignmentCause!: TeamClientCauseModel;
}
